import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Eye, Play, Pause, RotateCcw, Trash2, Filter, Clock, CheckCircle, XCircle, AlertTriangle, Activity, BarChart3, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import DocumentUploadsService from "@/api/documentUploads.service";

interface QueueItem {
  id: number;
  publicId: string;
  documentUploadId: number;
  priority: number;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'paused';
  processingStarted?: string;
  processingCompleted?: string;
  errorMessage?: string;
  retryCount: number;
  maxRetries: number;
  estimatedCompletionTime?: string;
  processingTimeMs?: number;
  dateCreated: string;
  documentUpload?: {
    id: number;
    fileName: string;
    originalFileName: string;
    fileSize?: number;
    mimeType?: string;
    uploadMethod: string;
    documentType?: {
      id: number;
      name: string;
    };
  };
}

interface QueueStats {
  totalItems: number;
  queuedItems: number;
  processingItems: number;
  completedItems: number;
  failedItems: number;
  pausedItems: number;
  averageProcessingTime: number;
  throughputPerHour: number;
  errorRate: number;
  queueHealth: 'healthy' | 'warning' | 'critical';
}

interface ProcessingMetrics {
  hourlyThroughput: Array<{
    hour: string;
    processed: number;
    failed: number;
  }>;
  processingTimes: Array<{
    documentType: string;
    averageTime: number;
    minTime: number;
    maxTime: number;
  }>;
  errorBreakdown: Array<{
    errorType: string;
    count: number;
    percentage: number;
  }>;
}

export default function ProcessingQueue() {
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [metrics, setMetrics] = useState<ProcessingMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [selectedItem, setSelectedItem] = useState<QueueItem | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState("queue");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [bulkSelectedItems, setBulkSelectedItems] = useState<number[]>([]);

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchData();
      fetchStats();
      fetchMetrics();
    }
  }, [selectedBusiness]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (selectedBusiness) {
        fetchData();
        fetchStats();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedBusiness]);

  const fetchData = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await DocumentUploadsService.getProcessingQueue(selectedBusiness.id);
      if (response.data.result === "success") {
        setQueueItems(response.data.queueItems || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch processing queue",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await DocumentUploadsService.getQueueStats(selectedBusiness.id);
      if (response.data.result === "success") {
        setQueueStats(response.data.stats);
      }
    } catch (error) {
      console.error("Failed to fetch queue stats:", error);
    }
  };

  const fetchMetrics = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await DocumentUploadsService.getProcessingMetrics(selectedBusiness.id);
      if (response.data.result === "success") {
        setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error("Failed to fetch processing metrics:", error);
    }
  };

  const handlePauseQueue = async () => {
    try {
      await DocumentUploadsService.pauseProcessingQueue(selectedBusiness.id);
      toast({
        title: "Success",
        description: "Processing queue paused",
      });
      fetchData();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to pause processing queue",
        variant: "destructive",
      });
    }
  };

  const handleResumeQueue = async () => {
    try {
      await DocumentUploadsService.resumeProcessingQueue(selectedBusiness.id);
      toast({
        title: "Success",
        description: "Processing queue resumed",
      });
      fetchData();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resume processing queue",
        variant: "destructive",
      });
    }
  };

  const handleRetryItem = async (itemId: number) => {
    try {
      await DocumentUploadsService.retryQueueItem(itemId);
      toast({
        title: "Success",
        description: "Item queued for retry",
      });
      fetchData();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to retry item",
        variant: "destructive",
      });
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;

    try {
      await DocumentUploadsService.removeFromQueue(selectedItem.id);
      toast({
        title: "Success",
        description: "Item removed from queue",
      });
      setIsDeleteDialogOpen(false);
      fetchData();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove item from queue",
        variant: "destructive",
      });
    }
  };

  const handleBulkAction = async (action: string) => {
    if (bulkSelectedItems.length === 0) return;

    try {
      switch (action) {
        case 'retry':
          await Promise.all(bulkSelectedItems.map(id => DocumentUploadsService.retryQueueItem(id)));
          toast({
            title: "Success",
            description: `${bulkSelectedItems.length} items queued for retry`,
          });
          break;
        case 'remove':
          await Promise.all(bulkSelectedItems.map(id => DocumentUploadsService.removeFromQueue(id)));
          toast({
            title: "Success",
            description: `${bulkSelectedItems.length} items removed from queue`,
          });
          break;
        case 'priority':
          // Implementation for bulk priority change
          break;
      }
      setBulkSelectedItems([]);
      fetchData();
      fetchStats();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to perform bulk action",
        variant: "destructive",
      });
    }
  };

  const openViewDialog = (item: QueueItem) => {
    setSelectedItem(item);
    setIsViewDialogOpen(true);
  };

  const openDeleteDialog = (item: QueueItem) => {
    setSelectedItem(item);
    setIsDeleteDialogOpen(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'queued':
        return <Clock className="h-4 w-4" />;
      case 'processing':
        return <Activity className="h-4 w-4 animate-pulse" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'failed':
        return <XCircle className="h-4 w-4" />;
      case 'paused':
        return <Pause className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      queued: "bg-blue-50 text-blue-700 border-blue-200",
      processing: "bg-amber-50 text-amber-700 border-amber-200",
      completed: "bg-green-50 text-green-700 border-green-200",
      failed: "bg-red-50 text-red-700 border-red-200",
      paused: "bg-gray-50 text-gray-700 border-gray-200"
    };

    return (
      <Badge className={variants[status as keyof typeof variants] || variants.queued}>
        {getStatusIcon(status)}
        <span className="ml-1 capitalize">{status}</span>
      </Badge>
    );
  };

  const getPriorityBadge = (priority: number) => {
    const variants = {
      1: "bg-red-50 text-red-700 border-red-200",
      2: "bg-amber-50 text-amber-700 border-amber-200",
      3: "bg-blue-50 text-blue-700 border-blue-200",
      4: "bg-gray-50 text-gray-700 border-gray-200",
      5: "bg-gray-50 text-gray-700 border-gray-200"
    };

    const labels = {
      1: "Critical",
      2: "High",
      3: "Medium",
      4: "Low",
      5: "Lowest"
    };

    return (
      <Badge className={variants[priority as keyof typeof variants] || variants[3]}>
        {labels[priority as keyof typeof labels] || 'Medium'}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'text-green-600';
      case 'warning':
        return 'text-amber-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const filteredItems = queueItems.filter(item => {
    const matchesSearch = item.documentUpload?.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.documentUpload?.originalFileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.documentUpload?.documentType?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesPriority = priorityFilter === "all" || item.priority.toString() === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const queueColumns = [
    {
      id: "select",
      header: ({ table }: any) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={(e) => {
            table.toggleAllPageRowsSelected(!!e.target.checked);
            if (e.target.checked) {
              setBulkSelectedItems(filteredItems.map(item => item.id));
            } else {
              setBulkSelectedItems([]);
            }
          }}
        />
      ),
      cell: ({ row }: any) => (
        <input
          type="checkbox"
          checked={bulkSelectedItems.includes(row.original.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setBulkSelectedItems([...bulkSelectedItems, row.original.id]);
            } else {
              setBulkSelectedItems(bulkSelectedItems.filter(id => id !== row.original.id));
            }
          }}
        />
      ),
    },
    {
      accessorKey: "documentUpload",
      header: "Document",
      cell: ({ row }: any) => {
        const upload = row.getValue("documentUpload");
        return upload ? (
          <div className="flex items-center gap-2">
            <div>
              <div className="font-medium">{upload.originalFileName}</div>
              <div className="text-sm text-muted-foreground">
                {upload.documentType?.name || 'Unknown'} • {formatFileSize(upload.fileSize)}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground">No document</span>
        );
      },
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: ({ row }: any) => getPriorityBadge(row.getValue("priority")),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }: any) => getStatusBadge(row.getValue("status")),
    },
    {
      accessorKey: "processingTimeMs",
      header: "Processing Time",
      cell: ({ row }: any) => {
        const time = row.getValue("processingTimeMs");
        const status = row.original.status;
        
        if (status === 'processing' && row.original.processingStarted) {
          const elapsed = Date.now() - new Date(row.original.processingStarted).getTime();
          return <span className="text-amber-600">{formatDuration(elapsed)} (ongoing)</span>;
        } else if (time) {
          return formatDuration(time);
        } else {
          return 'N/A';
        }
      },
    },
    {
      accessorKey: "retryCount",
      header: "Retries",
      cell: ({ row }: any) => {
        const retryCount = row.getValue("retryCount");
        const maxRetries = row.original.maxRetries;
        return `${retryCount}/${maxRetries}`;
      },
    },
    {
      accessorKey: "dateCreated",
      header: "Created",
      cell: ({ row }: any) => formatDate(row.getValue("dateCreated")),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => {
        const item = row.original;
        const canRetry = item.status === 'failed' && item.retryCount < item.maxRetries;
        const canRemove = item.status !== 'processing';
        
        return (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openViewDialog(item)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            {canRetry && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRetryItem(item.id)}
                className="text-blue-600 hover:text-blue-700"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            )}
            {canRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDeleteDialog(item)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Processing Queue</h1>
          <p className="text-muted-foreground">
            Monitor and manage document processing queue
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={autoRefresh ? "bg-green-50 text-green-700" : ""}
          >
            <Activity className="h-4 w-4 mr-2" />
            Auto Refresh {autoRefresh ? 'On' : 'Off'}
          </Button>
          <Button variant="outline" size="sm" onClick={handlePauseQueue}>
            <Pause className="h-4 w-4 mr-2" />
            Pause Queue
          </Button>
          <Button variant="outline" size="sm" onClick={handleResumeQueue}>
            <Play className="h-4 w-4 mr-2" />
            Resume Queue
          </Button>
        </div>
      </div>

      {/* Queue Stats Overview */}
      {queueStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Items</p>
                  <p className="text-2xl font-bold">{queueStats.totalItems}</p>
                </div>
                <BarChart3 className="h-8 w-8 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Queued</p>
                  <p className="text-2xl font-bold text-blue-600">{queueStats.queuedItems}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Processing</p>
                  <p className="text-2xl font-bold text-amber-600">{queueStats.processingItems}</p>
                </div>
                <Activity className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Completed</p>
                  <p className="text-2xl font-bold text-green-600">{queueStats.completedItems}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-red-600">{queueStats.failedItems}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Health</p>
                  <p className={`text-lg font-bold capitalize ${getHealthColor(queueStats.queueHealth)}`}>
                    {queueStats.queueHealth}
                  </p>
                </div>
                <AlertTriangle className={`h-8 w-8 ${getHealthColor(queueStats.queueHealth)}`} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Performance Metrics */}
      {queueStats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Avg Processing Time</p>
                  <p className="text-xl font-bold">{formatDuration(queueStats.averageProcessingTime)}</p>
                </div>
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Throughput/Hour</p>
                  <p className="text-xl font-bold">{queueStats.throughputPerHour}</p>
                </div>
                <TrendingUp className="h-6 w-6 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Error Rate</p>
                  <p className="text-xl font-bold">{(queueStats.errorRate * 100).toFixed(1)}%</p>
                </div>
                <AlertTriangle className="h-6 w-6 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="queue">Queue Items</TabsTrigger>
          <TabsTrigger value="metrics">Metrics</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filters & Search
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="status-filter">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="queued">Queued</SelectItem>
                      <SelectItem value="processing">Processing</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="priority-filter">Priority</Label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All priorities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="1">Critical</SelectItem>
                      <SelectItem value="2">High</SelectItem>
                      <SelectItem value="3">Medium</SelectItem>
                      <SelectItem value="4">Low</SelectItem>
                      <SelectItem value="5">Lowest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  {bulkSelectedItems.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('retry')}
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Retry ({bulkSelectedItems.length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBulkAction('remove')}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Remove ({bulkSelectedItems.length})
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Queue Items Table */}
          <Card>
            <CardHeader>
              <CardTitle>Queue Items ({filteredItems.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <DataTable
                  columns={queueColumns}
                  data={filteredItems}
                  section="processing-queue"
                  isLoading={loading}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metrics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Processing Metrics</CardTitle>
              <p className="text-sm text-muted-foreground">
                Detailed analytics and performance metrics
              </p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <BarChart3 className="h-12 w-12 mx-auto mb-4" />
                <p>Detailed metrics visualization coming soon</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Queue Settings</CardTitle>
              <p className="text-sm text-muted-foreground">
                Configure processing queue parameters
              </p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4" />
                <p>Queue configuration settings coming soon</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* View Item Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Queue Item Details</DialogTitle>
            <DialogDescription>
              View processing queue item information
            </DialogDescription>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Document</Label>
                  <p className="text-sm">{selectedItem.documentUpload?.originalFileName || 'N/A'}</p>
                </div>
                <div>
                  <Label>Document Type</Label>
                  <p className="text-sm">{selectedItem.documentUpload?.documentType?.name || 'Unknown'}</p>
                </div>
                <div>
                  <Label>Priority</Label>
                  <div className="mt-1">
                    {getPriorityBadge(selectedItem.priority)}
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedItem.status)}
                  </div>
                </div>
                <div>
                  <Label>File Size</Label>
                  <p className="text-sm">{formatFileSize(selectedItem.documentUpload?.fileSize)}</p>
                </div>
                <div>
                  <Label>Upload Method</Label>
                  <p className="text-sm capitalize">{selectedItem.documentUpload?.uploadMethod || 'N/A'}</p>
                </div>
                <div>
                  <Label>Retries</Label>
                  <p className="text-sm">{selectedItem.retryCount}/{selectedItem.maxRetries}</p>
                </div>
                <div>
                  <Label>Created</Label>
                  <p className="text-sm">{formatDate(selectedItem.dateCreated)}</p>
                </div>
              </div>

              {selectedItem.processingStarted && (
                <div>
                  <Label>Processing Started</Label>
                  <p className="text-sm">{formatDate(selectedItem.processingStarted)}</p>
                </div>
              )}

              {selectedItem.processingCompleted && (
                <div>
                  <Label>Processing Completed</Label>
                  <p className="text-sm">{formatDate(selectedItem.processingCompleted)}</p>
                </div>
              )}

              {selectedItem.processingTimeMs && (
                <div>
                  <Label>Processing Time</Label>
                  <p className="text-sm">{formatDuration(selectedItem.processingTimeMs)}</p>
                </div>
              )}

              {selectedItem.errorMessage && (
                <div>
                  <Label>Error Message</Label>
                  <div className="mt-1 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-700">{selectedItem.errorMessage}</p>
                  </div>
                </div>
              )}

              {selectedItem.estimatedCompletionTime && (
                <div>
                  <Label>Estimated Completion</Label>
                  <p className="text-sm">{formatDate(selectedItem.estimatedCompletionTime)}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Item Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Queue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this item from the processing queue? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem} className="bg-red-600 hover:bg-red-700">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
} 